import { isNullish, useToast } from '@arnold/common';
import { getEncodedCryptoAES, getWorkingDay, seqDateToString } from '@arnold/core';
import CryptoAES from 'crypto-js/aes';
import CryptoENC from 'crypto-js/enc-utf8';
import { addDays, addMinutes } from 'date-fns';
import { uniq, uniqBy } from 'ramda';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  AnonymityLevel,
  ConversationSubjectType,
  FeEventType,
  GetProcessReportRespondentsQuery,
  MessageEventType,
  MessageTemplateUid,
  ReportSequenceRespondentDetailQueryQuery,
  ReportSequenceSubscriptionSubscription,
  Survey,
  SurveyStatus,
  TopicGroupTypeCode,
  useCreateSurveyMutation,
  useLastMessagesForRespondentLazyQuery,
  useLogFeEventMutation,
  useReopenSurveyMutation,
  useSendInvitationAgainMutation,
  useUpdateSurveyMutation,
} from '../../generated/hooks';
import { getTeamNameByRespondentId } from '../../lib/reportHelper';

type ReportSequence =
  | NonNullable<ReportSequenceRespondentDetailQueryQuery['getProcessReportRespondentDetail']>
  | NonNullable<NonNullable<ReportSequenceSubscriptionSubscription>['getProcessReport']>;

type GroupedRespondents = GetProcessReportRespondentsQuery['getProcessReportRespondents']['data'][0];

interface IProps {
  reportSequence: ReportSequence;
  groupedRespondents: GetProcessReportRespondentsQuery['getProcessReportRespondents']['data'];
  accessToken: string;
  encryptedRespondentId?: string;
  urlParams: string;
  respondentLink?: boolean;
  publicAccess?: boolean;
  loading?: boolean;
  showDeleteButton?: boolean;
  onReload: (shoudReload?: boolean) => void;
  reloadRespondentsData?: () => void;
  isDetail?: boolean;
  createDetailUrl?: (topicGroupId: string) => string;
}

type RespondentWithData = {
  respondent: GroupedRespondents['respondents'][0];
  group: GroupedRespondents;
};
type RespondentSurvey = {
  respondentId: string;
  surveyId: string;
};
const RespondentsFeelings = ({
  reportSequence,
  groupedRespondents,
  accessToken,
  encryptedRespondentId,
  urlParams,
  respondentLink,
  publicAccess,
  loading,
  showDeleteButton,
  createDetailUrl,
  onReload,
  reloadRespondentsData,
  isDetail,
}: IProps) => {
  const [modal, setModal] = useState<boolean>(false);
  const [respondentToDelete, setRespondentToDelete] = useState<
    GetProcessReportRespondentsQuery['getProcessReportRespondents']['data'][0]['respondents'][0] | null
  >(null);
  const [resendingInvitations, setResendingInvitations] = useState<RespondentSurvey[]>([]);
  const [deliveredInvitations, setDeliveredInvitations] = useState<RespondentSurvey[]>([]);
  const [getLastMessages] = useLastMessagesForRespondentLazyQuery();
  const [dateFrom, setDateFrom] = useState<Date>(new Date());
  const [logFeEvent] = useLogFeEventMutation();

  const handleLogFeEvent = (eventType: FeEventType, respondentId: string) => {
    try {
      logFeEvent({
        variables: {
          eventType,
          payload: {
            respondentId,
            surveyGroupId: reportSequence.surveyGroup?.id,
            organizationId: reportSequence.metadata.organization.id,
          },
        },
      });
    } catch (err) {
      // Do nothing
    }
  };
  const showModal = (
    respondent: GetProcessReportRespondentsQuery['getProcessReportRespondents']['data'][0]['respondents'][0],
    group: RespondentWithData,
  ) => {
    setModal(true);
    setRespondentToDelete(respondent);
    const answeredSurveys = group.group.surveys.filter(
      (survey) =>
        survey.publicChannels.some((ch) => ch.respondentId === respondent.id && ch.withAnswer) &&
        [SurveyStatus.Completed, SurveyStatus.Ongoing].includes(survey.status),
    );

    const isPrepared = group.group.surveys.every(
      (survey) =>
        [SurveyStatus.Prepared, SurveyStatus.Notstarted, SurveyStatus.Activating].includes(survey.status) ||
        answeredSurveys.length === 0,
    );

    const date = isPrepared
      ? new Date(group.group.addedOn)
      : answeredSurveys.length > 0
        ? addDays(
            new Date(
              answeredSurveys
                .map((survey) => survey.endsAt)
                .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0],
            ),
            1,
          )
        : new Date();
    setDateFrom(date);
  };

  const respondentId =
    publicAccess && encryptedRespondentId
      ? CryptoAES.decrypt(decodeURIComponent(encryptedRespondentId), reportSequence.id.toString()).toString(CryptoENC)
      : encryptedRespondentId;
  const { t } = useTranslation('Report');
  const history = useHistory();
  const flattenedRespondents =
    groupedRespondents
      ?.reduce(
        (acc: RespondentWithData[], group: GroupedRespondents) => [
          ...acc,
          ...group.respondents.map((respondent) => ({ respondent, group })),
        ],
        [] as RespondentWithData[],
      )
      .filter((r) => !respondentId || r.respondent.id === respondentId) ?? [];
  const respondents = uniqBy((r) => `${r.respondent.id}-${r.group.addedOn}`, flattenedRespondents);

  const accessPath = publicAccess ? 'process-results' : 'report-sequence';
  const containsAnonymity = reportSequence.surveyGroup?.steps.some((step) =>
    step.processStep.stepTopicGroup.allowedAnonymities.includes(AnonymityLevel.Organization),
  );

  const isHiring = reportSequence.surveyGroup?.topicGroup.typeCode === TopicGroupTypeCode.Hiring;

  const { addToast } = useToast();
  const [callReopenMutation] = useReopenSurveyMutation();
  const [createSurvey] = useCreateSurveyMutation();
  const [updateSurvey] = useUpdateSurveyMutation();
  const [sendAgain] = useSendInvitationAgainMutation();
  const [clicked, setClicked] = useState(false);
  const [editRespondent, setEditRespondent] = useState<{
    respondent: GetProcessReportRespondentsQuery['getProcessReportRespondents']['data'][0]['respondents'][0];
    contact: string;
  } | null>(null);

  useEffect(() => {
    const interval = setTimeout(() => {
      if (resendingInvitations.length === 0) return;
      getLastMessages({
        variables: {
          surveyIds: resendingInvitations.map((x) => x.surveyId),
          respondentIds: resendingInvitations.map((x) => x.respondentId),
          accessToken,
        },
        onCompleted: (data) => {
          if (
            data.lastMessagesForRespondent.filter(
              (message) =>
                message.error ||
                [
                  MessageEventType.Bounced,
                  MessageEventType.Spam,
                  MessageEventType.Unsubscribed,
                  'undelivered',
                ].includes(message.messageStatus!),
            ).length > 2
          ) {
            addToast(t('contactSupport'));
          }
          setResendingInvitations((prev) =>
            prev.filter(
              (x) =>
                !data.lastMessagesForRespondent.some(
                  (y) => y.surveyId === x.surveyId && y.respondentId === x.respondentId && (y.error || y.messageStatus),
                ),
            ),
          );
          setDeliveredInvitations((prev) =>
            uniq([
              ...prev,
              ...data.lastMessagesForRespondent
                .filter((x) => x.messageStatus?.toUpperCase() === MessageEventType.Delivered)
                .map(({ surveyId, respondentId }) => ({ surveyId, respondentId })),
            ]),
          );
        },
      });
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resendingInvitations, accessToken]);

  const r = respondents.map((respondent, i) => {
    const seqDate = seqDateToString(new Date(respondent.group.addedOn));
    const teamName = getTeamNameByRespondentId(
      reportSequence as NonNullable<ReportSequenceRespondentDetailQueryQuery['getProcessReportRespondentDetail']>,
      respondent.respondent.id,
      t,
    );
    const respId = publicAccess
      ? encryptedRespondentId
        ? encryptedRespondentId
        : publicAccess && !encryptedRespondentId
          ? getEncodedCryptoAES(respondent.respondent.id, reportSequence.id)
          : respondent.respondent.id
      : respondent.respondent.id;

    const metricAnswers = respondent.group.metricQuestionsForRespondent?.find(
      (m) => m.respondentId === respondent.respondent.id,
    );

    const sortedSurveys =
      respondent.group.surveys ||
      respondent.group.hiringSurveys ||
      ([] as Survey[]).slice().sort((a, b) => new Date(b.endsAt).getTime() - new Date(a.endsAt).getTime());
    const isDeleteDisabled =
      sortedSurveys.length === 0
        ? false
        : sortedSurveys.every(
            (s) =>
              [SurveyStatus.Completed, SurveyStatus.Archived].includes(s.status) ||
              s.publicChannels.some((ch) => ch.respondentId === respondent.respondent.id && ch.withAnswer),
          );

    const firstSurveySentAt = isHiring
      ? respondent.group.hiringSurveys
          ?.filter((survey) => survey.respondentIds.includes(respondent.respondent.id))
          .sort((a, b) => new Date(a.sendAt).getTime() - new Date(b.sendAt).getTime())[0]?.sendAt
      : undefined;

    const position = respondent.group.hiringAdditionalData?.find(
      (data) => data?.respondentId === respondent.respondent.id,
    )?.position;
    const isHired = respondent.group.hiringAdditionalData?.find(
      (data) => data?.respondentId === respondent.respondent.id,
    )?.isHired;
    const isRejected = respondent.group.hiringAdditionalData?.find(
      (data) => data?.respondentId === respondent.respondent.id,
    )?.isRejected;
    const recruiterName = respondent.group.hiringAdditionalData?.find(
      (data) => data?.respondentId === respondent.respondent.id,
    )?.recruiterName;

    const hasUnreadMessage =
      !isDetail &&
      (respondent.group.hasUnreadMessages?.find((unread) => unread.respondentId === respondent.respondent.id)
        ?.unreadMessages?.length || 0) > 0;

    const handleRowClick = () => {
      history.push(`/${accessPath}/${accessToken}/respondent/${respId}/${seqDate}${urlParams}`);
    };

    return {
      firstSurveySentAt,
      position,
      isHired,
      isRejected,
      hasUnreadMessage,
      handleRowClick,
      respondent,
      seqDate,
      respId,
      teamName,
      isDeleteDisabled,
      recruiterName,
      steps:
        reportSequence.surveyGroup?.steps.map((step) => {
          const survey = (isHiring ? respondent.group.hiringSurveys : respondent.group.surveys)?.find(
            (s) =>
              s.topicGroup?.id === step.processStep.stepTopicGroup.id &&
              s.respondentIds.includes(respondent.respondent.id),
          );
          const channel = survey?.publicChannels?.find((ch) => ch.respondentId === respondent.respondent.id);
          const unread =
            channel &&
            respondent.group.hasUnreadMessages
              ?.find((unread) => unread.respondentId === respondent.respondent.id)
              ?.unreadMessages?.includes(channel.id);
          const undelivered = respondent.group.undeliveredInvitations
            ?.find((messages) => messages?.respondentId === respondent.respondent.id)
            ?.undeliveredSurveyInvitations?.find(
              (inv) =>
                inv.surveyId === survey?.id &&
                !deliveredInvitations.some(
                  (dInv) => dInv.respondentId === respondent.respondent.id && survey?.id === dInv.surveyId,
                ),
            );
          const isResending = resendingInvitations.some(
            (dInv) => dInv.respondentId === respondent.respondent.id && survey?.id === dInv.surveyId,
          );
          const disabled =
            channel?.lastRemindedAt && !undelivered
              ? addDays(new Date(channel?.lastRemindedAt), 1).getTime() >= new Date().getTime()
              : false;
          const maxWeight = channel?.maxAnonymousHighlightedLevel ?? 0;

          const metricAnswer = metricAnswers?.answers.find(
            (a) => a.topicGroupId === step.processStep.stepTopicGroup.id,
          );

          const suffix = urlParams
            ? urlParams.startsWith('?')
              ? `&${urlParams.slice(1)}`
              : urlParams.startsWith('&')
                ? urlParams
                : `&${urlParams}`
            : '';
          const detailUrl = createDetailUrl
            ? createDetailUrl(step.processStep.stepTopicGroup.id)
            : `/${accessPath}/${accessToken}/respondent/${respId}/${seqDate}?selected_tg=${step.processStep.stepTopicGroup.id}${suffix}`;
          const sendAgainSurvey = async () => {
            if (!survey) {
              return;
            }
            setClicked(true);
            const toastMessage = t('resendToast') + new Date(survey.endsAt).toLocaleDateString() + '.';
            sendAgain({
              refetchQueries: ['getProcessReportRespondents', 'ReportSequenceRespondentDetailQuery'],
              awaitRefetchQueries: true,
              variables: {
                surveyId: survey.id,
                accessToken: reportSequence.identifier,
                respondentId: respondent.respondent.id,
                templateUid: reportSequence.metadata.respondent ? MessageTemplateUid.ReminderFromManager : null,
              },
              onCompleted: () => {
                setClicked(false);
                addToast(toastMessage, undefined, undefined, undefined, true);
                setResendingInvitations((prev) => [
                  ...prev,
                  { respondentId: respondent.respondent.id, surveyId: survey.id },
                ]);
                handleLogFeEvent(FeEventType.ReportSendReminder, respondent.respondent.id);
              },
              onError: (e) => {
                setClicked(false);
              },
            });
          };

          const reopenSurvey = async () => {
            if (!survey) {
              return;
            }
            setClicked(true);
            const endsAt = new Date(new Date().getTime() + 3 * 24 * 60 * 60 * 1000);
            endsAt.setHours(new Date(survey?.endsAt).getHours());
            endsAt.setMinutes(0);
            endsAt.setSeconds(0);
            endsAt.setMilliseconds(0);
            const toastMessage = t('resendToast') + new Date(endsAt).toLocaleDateString() + '.';
            callReopenMutation({
              refetchQueries: ['getProcessReportRespondents', 'ReportSequenceRespondentDetailQuery'],
              awaitRefetchQueries: true,
              variables: {
                input: {
                  surveyId: survey.id,
                  accessToken: reportSequence.identifier,
                  endsAt,
                  sendMessage: true,
                  respondentIds: [respondent.respondent.id],
                  messageTemplateUid: reportSequence.metadata.respondent
                    ? MessageTemplateUid.ReminderFromManager
                    : null,
                },
              },
              onCompleted: () => {
                setClicked(false);
                addToast(toastMessage, undefined, undefined, undefined, true);
                setResendingInvitations((prev) => [
                  ...prev,
                  { respondentId: respondent.respondent.id, surveyId: survey.id },
                ]);
                handleLogFeEvent(FeEventType.ReportReopenSurvey, respondent.respondent.id);
              },
              onError: (e) => {
                setClicked(false);
              },
            });
          };
          const createMissingSurvey = async () => {
            setClicked(true);
            const processStep = step.processStep;
            if (isNullish(processStep.startOffsetMins) || isNullish(processStep.endOffsetMins)) {
              return null;
            }
            const addedOn = new Date(respondent.group.addedOn);
            let sendAt = addMinutes(addedOn, processStep.startOffsetMins);
            if (addedOn.getTimezoneOffset() !== sendAt.getTimezoneOffset()) {
              sendAt = addMinutes(sendAt, sendAt.getTimezoneOffset() - addedOn.getTimezoneOffset());
            }
            // Set date of sendAt to today if it is in the past
            const isInPast = sendAt < new Date();
            if (isInPast) {
              sendAt.setFullYear(new Date().getFullYear());
              sendAt.setMonth(new Date().getMonth());
              sendAt.setDate(new Date().getDate());
            }
            const reminders: { reminderAt: Date }[] = [];
            let remindersShift = 0;

            processStep.reminders.forEach((reminder) => {
              const { workingDay: reminderDate, shiftInDays: reminderShift } = getWorkingDay(
                addMinutes(addDays(sendAt, remindersShift), reminder.reminderOffsetMins ?? 0),
              );
              if (reminder.reminderOffsetMins) {
                reminders.push({
                  reminderAt: reminderDate,
                });
                remindersShift = remindersShift + reminderShift;
              }
            });

            const endsAt = getWorkingDay(
              addMinutes(addDays(sendAt, remindersShift), processStep.endOffsetMins),
            ).workingDay;

            if (isInPast) {
              sendAt = new Date();
            }
            const toastMessage = t('resendToast') + new Date(endsAt).toLocaleDateString() + '.';

            createSurvey({
              refetchQueries: ['getProcessReportRespondents', 'ReportSequenceRespondentDetailQuery'],
              awaitRefetchQueries: true,
              variables: {
                input: {
                  sendAt,
                  endsAt,
                  reminders,
                  accessToken: reportSequence.identifier,
                  surveyGroupId: reportSequence.surveyGroup?.id!,
                  conversationSubjectId: processStep.stepTopicGroup.id,
                  conversationSubjectType: ConversationSubjectType.TopicGroup,
                  welcomeTemplateUid: reportSequence.metadata.respondent
                    ? MessageTemplateUid.ReminderFromManager
                    : null,
                  forceActivation: true,
                  organizationId: reportSequence.metadata.organization.id,
                  seqDate: addedOn,
                  respondentIds: [respondent.respondent.id],
                },
              },
              onCompleted: () => {
                setClicked(false);
                addToast(toastMessage, undefined, undefined, undefined, true);
                if (reloadRespondentsData) {
                  reloadRespondentsData();
                }
                handleLogFeEvent(FeEventType.ReportReopenSurvey, respondent.respondent.id);
              },
              onError: (e) => {
                setClicked(false);
              },
            });
          };

          const updateNotStartedSurvey = async () => {
            setClicked(true);
            const processStep = step.processStep;
            if (isNullish(processStep.startOffsetMins) || isNullish(processStep.endOffsetMins)) {
              return null;
            }
            const addedOn = new Date(respondent.group.addedOn);
            let sendAt = addMinutes(addedOn, processStep.startOffsetMins);
            if (addedOn.getTimezoneOffset() !== sendAt.getTimezoneOffset()) {
              sendAt = addMinutes(sendAt, sendAt.getTimezoneOffset() - addedOn.getTimezoneOffset());
            }
            // Set date of sendAt to today if it is in the past
            const isInPast = sendAt < new Date();
            if (isInPast) {
              sendAt.setFullYear(new Date().getFullYear());
              sendAt.setMonth(new Date().getMonth());
              sendAt.setDate(new Date().getDate());
            }

            const reminders: { reminderAt: Date }[] = [];
            let remindersShift = 0;

            processStep.reminders.forEach((reminder) => {
              const { workingDay: reminderDate, shiftInDays: reminderShift } = getWorkingDay(
                addMinutes(addDays(sendAt, remindersShift), reminder.reminderOffsetMins ?? 0),
              );
              if (reminder.reminderOffsetMins) {
                reminders.push({
                  reminderAt: reminderDate,
                });
                remindersShift = remindersShift + reminderShift;
              }
            });
            const endsAt = getWorkingDay(
              addMinutes(addDays(sendAt, remindersShift), processStep.endOffsetMins),
            ).workingDay;

            if (isInPast) {
              sendAt = new Date();
            }
            const toastMessage = t('resendToast') + new Date(endsAt).toLocaleDateString() + '.';

            updateSurvey({
              refetchQueries: ['getProcessReportRespondents', 'ReportSequenceRespondentDetailQuery'],
              awaitRefetchQueries: true,
              variables: {
                surveyId: survey?.id!,
                input: {
                  sendAt,
                  endsAt,
                  reminders,
                  accessToken: reportSequence.identifier,
                  welcomeTemplateUid: reportSequence.metadata.respondent
                    ? MessageTemplateUid.ReminderFromManager
                    : null,
                  forceActivation: true,
                  respondentIds: [respondent.respondent.id],
                },
              },
              onCompleted: () => {
                setClicked(false);
                addToast(toastMessage, undefined, undefined, undefined, true);
                if (reloadRespondentsData) {
                  reloadRespondentsData();
                }
                handleLogFeEvent(FeEventType.ReportReopenSurvey, respondent.respondent.id);
              },
              onError: (e) => {
                setClicked(false);
              },
            });
          };
          return {
            step,
            survey,
            position,
            channel,
            unread,
            undelivered,
            isResending,
            disabled,
            maxWeight,
            metricAnswer,
            detailUrl,
            sendAgainSurvey,
            reopenSurvey,
            createMissingSurvey,
            updateNotStartedSurvey,
          };
        }) ?? null,
    };
  });

  return {
    respondents: r,
    showModal,
    editRespondent,
    setEditRespondent,
    respondentToDelete,
    isHiring,
    setRespondentToDelete,
    modal,
    dateFrom,
    loading,
    clicked,
    containsAnonymity,
    setModal,
    onEditRespondent: () => {
      setEditRespondent(null);
      if (reloadRespondentsData) {
        reloadRespondentsData();
      } else {
        onReload(false);
      }
    },
  };
};

export default RespondentsFeelings;
